import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Link,
  Divider
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  OpenInNew as OpenInNewIcon,
  Visibility as VisibilityIcon,
  DesignServices as DesignServicesIcon
} from '@mui/icons-material';
import {
  getTypeLabel,
  getImpactLevelColor,
  formatEvidence,
  extractJiraKey,
  getJiraUrl
} from './ImpactAnalysisUtils';

/**
 * Individual impact card component
 */
const ImpactCard = ({ 
  impact, 
  sourceItem, 
  sourceJiraKey,
  sourceTitle,
  jiraBaseUrl,
  projectId,
  onViewPKB,
  onFeedback
}) => {
  const targetJiraKey = impact.jira_issue_key || extractJiraKey(impact);
  const targetTitle = impact.title || 'Unknown';
  const jiraUrl = getJiraUrl(targetJiraKey, jiraBaseUrl);
  const evidence = formatEvidence(impact, sourceTitle);
  
  return (
    <Paper 
      sx={{ 
        p: 2.5, 
        mb: 2, 
        border: '1px solid', 
        borderColor: 'divider',
        borderRadius: 2,
        '&:hover': {
          boxShadow: 2
        }
      }}
    >
      {/* Source → Target Context Header */}
      <Box
        sx={(theme) => ({
          mb: 2,
          p: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
        })}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Changed item:
            </Typography>
            {sourceJiraKey ? (
              <Chip
                label={sourceJiraKey}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            ) : null}
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {sourceTitle || 'Current PRD'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Impacted item:
            </Typography>
            {targetJiraKey ? (
              <Chip
                label={targetJiraKey}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            ) : null}
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {targetTitle}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Type and Impact Level */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip 
          label={getTypeLabel(impact)}
          size="small"
          color="primary"
          variant="outlined"
        />
        <Chip 
          label={`Impact: ${impact.impact_level || 'Unknown'}`}
          size="small"
          color={getImpactLevelColor(impact.impact_level)}
        />
        {typeof impact.similarity_score === 'number' && (
          <Chip 
            label={`Relevance: ${Math.round(impact.similarity_score * 100)}%`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
        {impact.impact_bucket ? (
          <Chip
            label={`Bucket: ${impact.impact_bucket}`}
            size="small"
            color={impact.impact_bucket === 'confident' ? 'success' : impact.impact_bucket === 'secondary' ? 'warning' : impact.impact_bucket === 'possible' ? 'info' : 'default'}
            variant="outlined"
          />
        ) : null}
        {evidence.hasDesignEvidence && (
          <Chip
            icon={<DesignServicesIcon />}
            label="Figma evidence"
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
      </Box>

      {/* Description */}
      {impact.description && (
        <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
          {impact.description}
        </Typography>
      )}

      {/* Matched Clauses */}
      {evidence.matchedClauses.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Matched clauses
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {evidence.matchedClauses.map((clause, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Typography variant="caption" sx={{ minWidth: 60, fontWeight: 600, color: 'text.secondary' }}>
                  {targetJiraKey || 'Item'} {clause.id}:
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, fontStyle: 'italic', color: 'text.primary' }}>
                  "{clause.text}"
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Figma / Design Evidence */}
      {evidence.designClauses.length > 0 && (
        <Box
          sx={(theme) => ({
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'secondary.light',
            bgcolor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
          })}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <DesignServicesIcon fontSize="small" color="secondary" />
            Figma/design evidence
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {evidence.designClauses.map((clause, idx) => (
              <Typography key={idx} variant="body2" sx={{ color: 'text.primary', fontStyle: 'italic' }}>
                "{clause.text}"
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {/* Why this is impacted */}
      {evidence.explanation && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Why this is impacted
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
            {evidence.explanation}
          </Typography>
        </Box>
      )}

      {/* Affected Fields */}
      {impact.affected_fields && impact.affected_fields.length > 0 && (
        <Box
          sx={(theme) => ({
            mb: 2,
            p: 1,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
          })}
        >
          <Typography variant="caption" sx={{ color: 'text.primary' }}>
            Affected areas: {impact.affected_fields.join(', ')}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Feedback Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={() => onFeedback && onFeedback(impact, 'useful')}
          sx={{ fontSize: '0.75rem' }}
        >
          ✅ Accurate
        </Button>
        <Button 
          size="small" 
          variant="outlined" 
          color="warning"
          onClick={() => onFeedback && onFeedback(impact, 'not_impacted')}
          sx={{ fontSize: '0.75rem' }}
        >
          ❌ Not accurate
        </Button>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<VisibilityIcon />}
          onClick={() => onViewPKB && onViewPKB(impact)}
        >
          {impact.type === 'test_case' ? 'View Test Case' : 'View PKB Entry'}
        </Button>
        {jiraUrl && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            component={Link}
            href={jiraUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in JIRA
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default ImpactCard;

